import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarClock } from 'lucide-react';
import { fetchUpcomingChecks } from '../../api/checks';
import type { UpcomingChecksSummary } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { hasPermission } from '../../lib/permissions';

function formatMoney(value: number): string {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatShortDate(value: string | null): string {
  if (!value) return '—';
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * C9 Dashboard "Vadesi Yaklaşan Çek/Senet" kartı (upcoming_checks widget'i):
 * penceredeki adet + alınan/verilen toplamları + en yakın 3 kayıt.
 * İzin yoksa veya veri yoksa kart gösterilmez (dashboard'u bozmaz).
 */
export function UpcomingChecksCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'checks', 'view');

  const [summary, setSummary] = useState<UpcomingChecksSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchUpcomingChecks(30)
      .then((res) => {
        if (!cancelled) setSummary(res.summary);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canView]);

  if (!canView || failed) return null;
  if (!loading && (!summary || summary.adet === 0)) return null;

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="dashboard-upcoming-checks">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-slate-400" aria-hidden="true" />
          <h3 className="text-sm font-semibold text-slate-900">Vadesi Yaklaşan Çek/Senet</h3>
        </div>
        <button
          type="button"
          data-testid="dashboard-upcoming-checks-all"
          onClick={() => navigate('/checks')}
          className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          Tümünü gör
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>

      {loading || !summary ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-3 gap-2 text-center" data-testid="dashboard-upcoming-checks-summary">
            <div className="rounded-lg bg-slate-50 p-2">
              <div className="text-[11px] font-semibold uppercase text-slate-500">Adet</div>
              <div className="text-sm font-bold text-slate-900">{summary.adet}</div>
            </div>
            <div className="rounded-lg bg-emerald-50 p-2">
              <div className="text-[11px] font-semibold uppercase text-emerald-600">Alınan</div>
              <div className="text-sm font-bold text-emerald-700">
                {formatMoney(summary.toplam_alinan)} ₺
              </div>
            </div>
            <div className="rounded-lg bg-red-50 p-2">
              <div className="text-[11px] font-semibold uppercase text-red-600">Verilen</div>
              <div className="text-sm font-bold text-red-700">
                {formatMoney(summary.toplam_verilen)} ₺
              </div>
            </div>
          </div>
          <div className="space-y-1">
            {summary.en_yakin.map((check) => (
              <div
                key={check.id}
                className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50"
                data-testid={`dashboard-upcoming-check-${check.id}`}
              >
                <div className="min-w-0">
                  <div className="truncate font-medium text-slate-800">{check.check_no}</div>
                  <div className="truncate text-xs text-slate-400">{check.drawer}</div>
                </div>
                <div className="flex-none text-right">
                  <div
                    className={`text-sm font-semibold ${
                      check.direction === 'alinan' ? 'text-emerald-600' : 'text-red-600'
                    }`}
                  >
                    {check.direction === 'alinan' ? '+' : '−'}
                    {formatMoney(check.amount ?? 0)} {check.currency}
                  </div>
                  <div className="text-xs text-slate-400">{formatShortDate(check.due_date)}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
